// Pure radar geometry for both the coverage renderer and future scan/event-loop
// work.  Keep MapLibre and entity DTOs out of this file: it should be possible to
// ask the same deterministic question in a worker, a test, or the map renderer.
//
// This is a planning model, not a claim about classified performance.  It
// intersects four public, explainable limits:
//   1. standard-atmosphere radio line of sight (configurable k-effective Earth),
//   2. antenna elevation and azimuth scan limits,
//   3. true slant/instrumented range, and
//   4. an optional reference-target range scaled by the radar equation's R^4 law.
// Terrain masking, clutter, weather, jamming, scan timing, and track confirmation
// remain explicit inputs/seams rather than being hidden inside a magic radius.

export const EARTH_RADIUS_KM = 6371.0088;
export const STANDARD_REFRACTION_K = 4 / 3;

export type RadarPlatform = 'ground' | 'naval' | 'airborne' | 'aerostat';

export interface RadarSpec {
  platform: RadarPlatform;
  /** Public/nominal detection range for the reference target, measured as slant range. */
  referenceRangeKm: number;
  /** Receiver/display hard limit. Defaults to referenceRangeKm when not public. */
  instrumentedRangeKm?: number;
  minRangeKm?: number;
  minElevationDeg: number;
  maxElevationDeg: number;
  azimuthDeg: number;
  bearingDeg: number;
  /** Absolute target-altitude cap in this simplified spherical model. */
  targetCeilingKm: number;
  /** Optional floor used only when a scenario deliberately approximates clutter/terrain. */
  minTargetAltitudeKm?: number;
  referenceRcsM2?: number;
  rangeResolutionM?: number;
  angularResolutionDeg?: number;
  scanIntervalS?: number;
}

export interface RadarPose {
  lon: number;
  lat: number;
  /** Sensor altitude above the model's reference surface, not aircraft service ceiling. */
  altitudeKm: number;
  /** Added to bearingDeg for a body-relative sector; zero for north-referenced sites. */
  headingDeg?: number;
}

export interface RadarTarget {
  lon: number;
  lat: number;
  altitudeKm: number;
  rcsM2?: number;
}

export interface RadarEnvironment {
  effectiveEarthK?: number;
  /** Terrain service can set this after testing the ray against a DEM. */
  terrainObstructed?: boolean;
}

export type DetectionReason =
  | 'eligible'
  | 'offline'
  | 'terrain_obstructed'
  | 'outside_azimuth'
  | 'below_horizon'
  | 'below_elevation'
  | 'above_elevation'
  | 'below_min_range'
  | 'beyond_instrumented_range'
  | 'beyond_reference_range'
  | 'below_altitude_floor'
  | 'above_altitude_ceiling';

export interface RadarGeometry {
  surfaceRangeKm: number;
  slantRangeKm: number;
  bearingDeg: number;
  elevationDeg: number;
  horizonRangeKm: number;
  lineOfSight: boolean;
}

export interface DetectionAssessment {
  eligible: boolean;
  reason: DetectionReason;
  reasons: DetectionReason[];
  geometry: RadarGeometry;
  effectiveRangeKm: number;
  /** Relative to the configured reference target/range; null without usable inputs. */
  signalMarginDb: number | null;
}

export interface AltitudeBand {
  groundKm: number;
  minAltitudeKm: number;
  maxAltitudeKm: number;
}

export interface RadarProfile {
  lower: AltitudeBand[];
  upper: AltitudeBand[];
  maxGroundRangeKm: number;
}

export interface ResolutionAssessment {
  resolved: boolean | null;
  rangeSeparated: boolean | null;
  angularSeparated: boolean | null;
  rangeDeltaM: number;
  angularDeltaDeg: number;
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

export function normalizeBearing(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

export function angularDifferenceDeg(a: number, b: number): number {
  return Math.abs(((normalizeBearing(a) - normalizeBearing(b) + 540) % 360) - 180);
}

export function effectiveEarthRadiusKm(k = STANDARD_REFRACTION_K): number {
  return EARTH_RADIUS_KM * Math.max(0.1, k);
}

export function haversineKm(lat1: number, lon1: number,
                            lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function bearingDegBetween(lat1: number, lon1: number,
                                  lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLon = (lon2 - lon1) * rad;
  const y = Math.sin(dLon) * Math.cos(lat2 * rad);
  const x = Math.cos(lat1 * rad) * Math.sin(lat2 * rad)
    - Math.sin(lat1 * rad) * Math.cos(lat2 * rad) * Math.cos(dLon);
  return normalizeBearing(Math.atan2(y, x) / rad);
}

/** Exact standard-refraction radio horizon along the effective spherical surface. */
export function radarHorizonKm(sensorAltitudeKm: number, targetAltitudeKm: number,
                               effectiveEarthK = STANDARD_REFRACTION_K): number {
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const hs = Math.max(0, sensorAltitudeKm);
  const ht = Math.max(0, targetAltitudeKm);
  const sensorAngle = Math.acos(clamp(r / (r + hs), -1, 1));
  const targetAngle = Math.acos(clamp(r / (r + ht), -1, 1));
  return r * (sensorAngle + targetAngle);
}

/** Target geometry in the sensor's local tangent frame on an effective Earth. */
export function geometryAtSurfaceDistance(sensorAltitudeKm: number,
                                          targetAltitudeKm: number,
                                          surfaceRangeKm: number,
                                          effectiveEarthK = STANDARD_REFRACTION_K):
    Pick<RadarGeometry, 'surfaceRangeKm' | 'slantRangeKm' | 'elevationDeg'
      | 'horizonRangeKm' | 'lineOfSight'> {
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const rs = r + Math.max(0, sensorAltitudeKm);
  const rt = r + Math.max(0, targetAltitudeKm);
  const theta = Math.max(0, surfaceRangeKm) / r;
  const x = rt * Math.sin(theta);
  const z = rt * Math.cos(theta) - rs;
  const slant = Math.hypot(x, z);
  const horizon = radarHorizonKm(sensorAltitudeKm, targetAltitudeKm, effectiveEarthK);
  return {
    surfaceRangeKm: Math.max(0, surfaceRangeKm),
    slantRangeKm: slant,
    elevationDeg: Math.atan2(z, Math.max(0, x)) * 180 / Math.PI,
    horizonRangeKm: horizon,
    lineOfSight: surfaceRangeKm <= horizon + 1e-7,
  };
}

export function radarGeometry(pose: RadarPose, target: RadarTarget,
                              env: RadarEnvironment = {}): RadarGeometry {
  const surfaceRangeKm = haversineKm(pose.lat, pose.lon, target.lat, target.lon);
  return {
    ...geometryAtSurfaceDistance(pose.altitudeKm, target.altitudeKm,
      surfaceRangeKm, env.effectiveEarthK),
    bearingDeg: bearingDegBetween(pose.lat, pose.lon, target.lat, target.lon),
  };
}

export function effectiveDetectionRangeKm(spec: RadarSpec, targetRcsM2?: number): number {
  const instrumented = Math.max(0, spec.instrumentedRangeKm ?? spec.referenceRangeKm);
  return Math.min(instrumented, referenceDetectionRangeKm(spec, targetRcsM2));
}

function referenceDetectionRangeKm(spec: RadarSpec, targetRcsM2?: number): number {
  let referenceLimited = Math.max(0, spec.referenceRangeKm);
  if (targetRcsM2 != null && targetRcsM2 > 0
      && spec.referenceRcsM2 != null && spec.referenceRcsM2 > 0) {
    referenceLimited *= (targetRcsM2 / spec.referenceRcsM2) ** 0.25;
  }
  return referenceLimited;
}

function signalMarginDb(spec: RadarSpec, slantRangeKm: number,
                        targetRcsM2?: number): number | null {
  if (!(spec.referenceRangeKm > 0) || !(slantRangeKm > 0)) return null;
  let margin = 40 * Math.log10(spec.referenceRangeKm / slantRangeKm);
  if (targetRcsM2 != null && targetRcsM2 > 0
      && spec.referenceRcsM2 != null && spec.referenceRcsM2 > 0) {
    margin += 10 * Math.log10(targetRcsM2 / spec.referenceRcsM2);
  }
  return margin;
}

/** Deterministic scan gate. Probability/track confirmation belongs above this layer. */
export function assessRadarDetection(spec: RadarSpec, pose: RadarPose,
                                     target: RadarTarget,
                                     env: RadarEnvironment = {},
                                     operational = true): DetectionAssessment {
  const geometry = radarGeometry(pose, target, env);
  const reasons: DetectionReason[] = [];
  if (!operational) reasons.push('offline');
  if (env.terrainObstructed) reasons.push('terrain_obstructed');

  const center = normalizeBearing(spec.bearingDeg + (pose.headingDeg ?? 0));
  if (spec.azimuthDeg < 360
      && angularDifferenceDeg(geometry.bearingDeg, center) > spec.azimuthDeg / 2 + 1e-9) {
    reasons.push('outside_azimuth');
  }
  if (!geometry.lineOfSight) reasons.push('below_horizon');
  if (geometry.elevationDeg < spec.minElevationDeg - 1e-9) reasons.push('below_elevation');
  if (geometry.elevationDeg > spec.maxElevationDeg + 1e-9) reasons.push('above_elevation');
  if (geometry.slantRangeKm < Math.max(0, spec.minRangeKm ?? 0) - 1e-9) {
    reasons.push('below_min_range');
  }
  const instrumented = Math.max(0, spec.instrumentedRangeKm ?? spec.referenceRangeKm);
  if (geometry.slantRangeKm > instrumented + 1e-9) reasons.push('beyond_instrumented_range');
  const effectiveRange = effectiveDetectionRangeKm(spec, target.rcsM2);
  if (geometry.slantRangeKm > referenceDetectionRangeKm(spec, target.rcsM2) + 1e-9) {
    reasons.push('beyond_reference_range');
  }
  if (target.altitudeKm < Math.max(0, spec.minTargetAltitudeKm ?? 0) - 1e-9) {
    reasons.push('below_altitude_floor');
  }
  if (target.altitudeKm > spec.targetCeilingKm + 1e-9) reasons.push('above_altitude_ceiling');

  return {
    eligible: reasons.length === 0,
    reason: reasons[0] ?? 'eligible',
    reasons: reasons.length ? reasons : ['eligible'],
    geometry,
    effectiveRangeKm: effectiveRange,
    signalMarginDb: signalMarginDb(spec, geometry.slantRangeKm, target.rcsM2),
  };
}

/** Height where a ray at `elevationDeg` crosses a radial line `surfaceRangeKm` away. */
export function rayAltitudeKm(sensorAltitudeKm: number, surfaceRangeKm: number,
                              elevationDeg: number,
                              effectiveEarthK = STANDARD_REFRACTION_K): number {
  if (surfaceRangeKm <= 1e-9 && Math.abs(elevationDeg) >= 89.999) {
    return elevationDeg > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const rs = r + Math.max(0, sensorAltitudeKm);
  const theta = Math.max(0, surfaceRangeKm) / r;
  const elev = clamp(elevationDeg, -89.999999, 89.999999) * Math.PI / 180;
  const denom = Math.cos(theta) - Math.sin(theta) * Math.tan(elev);
  if (denom <= 1e-12) return Number.POSITIVE_INFINITY;
  return rs / denom - r;
}

/** Endpoint reached at a fixed slant range/elevation in the local tangent frame. */
export function pointOnSlantRayKm(sensorAltitudeKm: number, slantRangeKm: number,
                                  elevationDeg: number,
                                  effectiveEarthK = STANDARD_REFRACTION_K):
    { groundKm: number; altitudeKm: number } {
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const rs = r + Math.max(0, sensorAltitudeKm);
  const slant = Math.max(0, slantRangeKm);
  const elevation = clamp(elevationDeg, -90, 90) * Math.PI / 180;
  const transverse = slant * Math.cos(elevation);
  const radial = rs + slant * Math.sin(elevation);
  const targetRadius = Math.hypot(transverse, radial);
  const centralAngle = Math.atan2(Math.max(0, transverse), radial);
  return { groundKm: r * centralAngle, altitudeKm: targetRadius - r };
}

/** Inner slant-range surface used to display a configured near-field blind zone. */
export function minimumRangeBoundaryProfile(spec: RadarSpec, sensorAltitudeKm: number,
                                            samples = 50,
                                            effectiveEarthK = STANDARD_REFRACTION_K):
    { ground: number[]; altitude: number[] } | null {
  const minRange = Math.max(0, spec.minRangeKm ?? 0);
  if (!(minRange > 0)) return null;
  const count = Math.max(4, Math.round(samples));
  const ground: number[] = [];
  const altitude: number[] = [];
  for (let i = 0; i <= count; i++) {
    const elevation = spec.minElevationDeg
      + (spec.maxElevationDeg - spec.minElevationDeg) * i / count;
    const point = pointOnSlantRayKm(sensorAltitudeKm, minRange,
      elevation, effectiveEarthK);
    ground.push(point.groundKm);
    altitude.push(point.altitudeKm);
  }
  return { ground, altitude };
}

/** Lowest target altitude whose endpoint-to-endpoint ray clears the effective Earth. */
export function horizonFloorAltitudeKm(sensorAltitudeKm: number, surfaceRangeKm: number,
                                       effectiveEarthK = STANDARD_REFRACTION_K): number {
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const rs = r + Math.max(0, sensorAltitudeKm);
  const theta = Math.max(0, surfaceRangeKm) / r;
  const sensorHorizonAngle = Math.acos(clamp(r / rs, -1, 1));
  const targetAngle = Math.max(0, theta - sensorHorizonAngle);
  if (targetAngle >= Math.PI / 2) return Number.POSITIVE_INFINITY;
  return Math.max(0, r / Math.cos(targetAngle) - r);
}

function rangeAltitudeBoundsKm(sensorAltitudeKm: number, surfaceRangeKm: number,
                               slantRangeKm: number,
                               effectiveEarthK = STANDARD_REFRACTION_K):
    { lower: number; upper: number } | null {
  const r = effectiveEarthRadiusKm(effectiveEarthK);
  const rs = r + Math.max(0, sensorAltitudeKm);
  const theta = Math.max(0, surfaceRangeKm) / r;
  const transverse = rs * Math.sin(theta);
  const disc = slantRangeKm ** 2 - transverse ** 2;
  if (disc < -1e-8) return null;
  const root = Math.sqrt(Math.max(0, disc));
  const center = rs * Math.cos(theta);
  return { lower: center - root - r, upper: center + root - r };
}

type BandConstraint = 'surface' | 'altitude_floor' | 'horizon'
  | 'min_elevation' | 'max_elevation' | 'range' | 'ceiling';

interface ConstrainedAltitudeBand {
  band: AltitudeBand;
  lowerConstraint: BandConstraint;
  upperConstraint: BandConstraint;
}

function constrainedAltitudeBand(spec: RadarSpec, sensorAltitudeKm: number,
                                 surfaceRangeKm: number,
                                 effectiveEarthK = STANDARD_REFRACTION_K):
    ConstrainedAltitudeBand | null {
  const maxRange = effectiveDetectionRangeKm(spec, spec.referenceRcsM2);
  const rangeBounds = rangeAltitudeBoundsKm(sensorAltitudeKm, surfaceRangeKm,
    maxRange, effectiveEarthK);
  if (!rangeBounds) return null;

  const lowerCandidates: Array<[number, BandConstraint]> = [
    [0, 'surface'],
    [spec.minTargetAltitudeKm ?? 0, 'altitude_floor'],
    [horizonFloorAltitudeKm(sensorAltitudeKm, surfaceRangeKm, effectiveEarthK), 'horizon'],
    [rayAltitudeKm(sensorAltitudeKm, surfaceRangeKm,
      spec.minElevationDeg, effectiveEarthK), 'min_elevation'],
    [rangeBounds.lower, 'range'],
  ];
  const upperCandidates: Array<[number, BandConstraint]> = [
    [spec.targetCeilingKm, 'ceiling'],
    [rayAltitudeKm(sensorAltitudeKm, surfaceRangeKm,
      spec.maxElevationDeg, effectiveEarthK), 'max_elevation'],
    [rangeBounds.upper, 'range'],
  ];
  let [lower, lowerConstraint] = lowerCandidates[0]!;
  for (const candidate of lowerCandidates.slice(1)) {
    if (candidate[0] > lower) [lower, lowerConstraint] = candidate;
  }
  let [upper, upperConstraint] = upperCandidates[0]!;
  for (const candidate of upperCandidates.slice(1)) {
    if (candidate[0] < upper) [upper, upperConstraint] = candidate;
  }
  if (!Number.isFinite(lower) || Number.isNaN(upper) || upper < lower - 1e-7) return null;
  return {
    band: {
      groundKm: Math.max(0, surfaceRangeKm),
      minAltitudeKm: lower,
      maxAltitudeKm: Math.max(lower, upper),
    },
    lowerConstraint,
    upperConstraint,
  };
}

/** Vertical coverage band at one ground distance for the configured reference target. */
export function radarAltitudeBand(spec: RadarSpec, sensorAltitudeKm: number,
                                  surfaceRangeKm: number,
                                  effectiveEarthK = STANDARD_REFRACTION_K): AltitudeBand | null {
  return constrainedAltitudeBand(spec, sensorAltitudeKm, surfaceRangeKm,
    effectiveEarthK)?.band ?? null;
}

function coverageSearchMaxKm(spec: RadarSpec): number {
  const r = Math.max(0, effectiveDetectionRangeKm(spec, spec.referenceRcsM2));
  return Math.max(0.1, r * 1.12 + 1);
}

function lastCoveredDistanceKm(spec: RadarSpec, sensorAltitudeKm: number,
                               effectiveEarthK: number): number {
  const max = coverageSearchMaxKm(spec);
  const probes = 512;
  let lastValid = 0;
  let firstInvalidAfter = max;
  let seen = false;
  for (let i = 0; i <= probes; i++) {
    const d = max * i / probes;
    const valid = radarAltitudeBand(spec, sensorAltitudeKm, d, effectiveEarthK) !== null;
    if (valid) { lastValid = d; seen = true; }
    else if (seen) { firstInvalidAfter = d; break; }
  }
  if (!seen) return 0;
  let lo = lastValid, hi = firstInvalidAfter;
  for (let i = 0; i < 36; i++) {
    const mid = (lo + hi) / 2;
    if (radarAltitudeBand(spec, sensorAltitudeKm, mid, effectiveEarthK)) lo = mid;
    else hi = mid;
  }
  return lo;
}

function constraintTransitionDistancesKm(spec: RadarSpec, sensorAltitudeKm: number,
                                         maxGroundKm: number,
                                         effectiveEarthK: number): number[] {
  if (!(maxGroundKm > 0)) return [];
  const probes = 1024;
  const result: number[] = [];
  let previousDistance = 0;
  let previous = constrainedAltitudeBand(spec, sensorAltitudeKm, 0, effectiveEarthK);
  for (let i = 1; i <= probes; i++) {
    const distance = maxGroundKm * i / probes;
    const current = constrainedAltitudeBand(spec, sensorAltitudeKm, distance, effectiveEarthK);
    if (previous && current
        && (previous.lowerConstraint !== current.lowerConstraint
          || previous.upperConstraint !== current.upperConstraint)) {
      const oldLower = previous.lowerConstraint;
      const oldUpper = previous.upperConstraint;
      let lo = previousDistance;
      let hi = distance;
      for (let j = 0; j < 36; j++) {
        const mid = (lo + hi) / 2;
        const detail = constrainedAltitudeBand(spec, sensorAltitudeKm, mid, effectiveEarthK);
        if (detail && detail.lowerConstraint === oldLower
            && detail.upperConstraint === oldUpper) lo = mid;
        else hi = mid;
      }
      result.push((lo + hi) / 2);
    }
    previousDistance = distance;
    previous = current;
  }
  return result;
}

function adaptiveProfileDistancesKm(spec: RadarSpec, sensorAltitudeKm: number,
                                    maxGroundKm: number, count: number,
                                    effectiveEarthK: number): number[] {
  if (!(maxGroundKm > 0)) return Array.from({ length: count + 1 }, () => 0);
  const targetSize = count + 1;
  const mandatory = [0, maxGroundKm,
    ...constraintTransitionDistancesKm(spec, sensorAltitudeKm,
      maxGroundKm, effectiveEarthK)]
    .filter((value) => Number.isFinite(value) && value >= 0 && value <= maxGroundKm)
    .sort((a, b) => a - b)
    .filter((value, index, values) => index === 0 || value - values[index - 1]! > 1e-6);

  // There are only a handful of active constraints, but retain both endpoints
  // if a deliberately pathological spec produces more transitions than the
  // requested display budget.
  let distances = mandatory;
  if (mandatory.length > targetSize) {
    distances = Array.from({ length: targetSize }, (_unused, index) =>
      mandatory[Math.round(index * (mandatory.length - 1) / (targetSize - 1))]!);
  }

  const bandAt = (distance: number): AltitudeBand => radarAltitudeBand(
    spec, sensorAltitudeKm, distance, effectiveEarthK)
    ?? { groundKm: distance, minAltitudeKm: sensorAltitudeKm,
      maxAltitudeKm: sensorAltitudeKm };
  while (distances.length < targetSize) {
    let splitIndex = 0;
    let bestScore = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < distances.length - 1; i++) {
      const leftDistance = distances[i]!;
      const rightDistance = distances[i + 1]!;
      const midpoint = (leftDistance + rightDistance) / 2;
      const left = bandAt(leftDistance);
      const right = bandAt(rightDistance);
      const actual = bandAt(midpoint);
      const interpolatedLower = (left.minAltitudeKm + right.minAltitudeKm) / 2;
      const interpolatedUpper = (left.maxAltitudeKm + right.maxAltitudeKm) / 2;
      const altitudeError = Math.max(
        Math.abs(actual.minAltitudeKm - interpolatedLower),
        Math.abs(actual.maxAltitudeKm - interpolatedUpper));
      // Once the constraint kinks are explicit, use a small gap term to keep
      // otherwise flat surfaces from becoming visibly coarse.
      const gapTerm = (rightDistance - leftDistance) / maxGroundKm * 0.01;
      const score = altitudeError + gapTerm;
      if (score > bestScore) { bestScore = score; splitIndex = i; }
    }
    const midpoint = (distances[splitIndex]! + distances[splitIndex + 1]!) / 2;
    distances = [...distances.slice(0, splitIndex + 1), midpoint,
      ...distances.slice(splitIndex + 1)];
  }
  return distances;
}

/** Sampled lower/upper surfaces used to build a 3D mesh. */
export function buildRadarProfile(spec: RadarSpec, sensorAltitudeKm: number,
                                  samples = 24,
                                  effectiveEarthK = STANDARD_REFRACTION_K): RadarProfile {
  const count = Math.max(4, Math.round(samples));
  const maxGround = lastCoveredDistanceKm(spec, sensorAltitudeKm, effectiveEarthK);
  const lower: AltitudeBand[] = [];
  const upper: AltitudeBand[] = [];
  const distances = adaptiveProfileDistancesKm(spec, sensorAltitudeKm,
    maxGround, count, effectiveEarthK);
  for (const d of distances) {
    const band = radarAltitudeBand(spec, sensorAltitudeKm, d, effectiveEarthK)
      ?? { groundKm: d, minAltitudeKm: sensorAltitudeKm,
        maxAltitudeKm: sensorAltitudeKm };
    lower.push({ ...band, maxAltitudeKm: band.minAltitudeKm });
    upper.push({ ...band, minAltitudeKm: band.maxAltitudeKm });
  }
  return { lower, upper, maxGroundRangeKm: maxGround };
}

/** Closed cross-section loop: lower skirt outward, upper shell inward. */
export function radarBoundaryProfile(spec: RadarSpec, sensorAltitudeKm: number,
                                     samples = 24,
                                     effectiveEarthK = STANDARD_REFRACTION_K):
    { ground: number[]; altitude: number[]; maxGroundRangeKm: number } {
  const profile = buildRadarProfile(spec, sensorAltitudeKm, samples, effectiveEarthK);
  const ground: number[] = [];
  const altitude: number[] = [];
  for (const p of profile.lower) { ground.push(p.groundKm); altitude.push(p.minAltitudeKm); }
  for (let i = profile.upper.length - 1; i >= 0; i--) {
    const p = profile.upper[i]!;
    ground.push(p.groundKm); altitude.push(p.maxAltitudeKm);
  }
  ground.push(ground[0] ?? 0);
  altitude.push(altitude[0] ?? sensorAltitudeKm);
  return { ground, altitude, maxGroundRangeKm: profile.maxGroundRangeKm };
}

export interface CoverageShapeParams {
  /** Slant range at the peak-gain elevation (the type's public reference range). */
  peakRangeKm: number;
  /** Elevation where range peaks; a fixed-face array reaches furthest a little
   *  above the horizon, not at it. */
  peakElevationDeg?: number;
  /** How sharply range tapers away from the peak: R = peak·cos(θ−peak)^exp. */
  rangeExponent?: number;
  minElevationDeg: number;
  maxElevationDeg: number;
}

/**
 * Analytic phased-array coverage cross-section in the sensor's vertical plane —
 * the smooth product-of-sigmoids lobe at its P=0.5 boundary, reduced to the
 * closed outline the mesh revolves. Slant range peaks near `peakElevationDeg`
 * and tapers as cos(θ−peak)^exp toward the ground and the zenith, so the shell
 * reads as a recognizable radar lobe rather than the old horizon skirt. Points
 * run apex → outer arc (low→high elevation) → apex, as ground distance and
 * altitude (km) per ring; revolve over the azimuth sector to get the 3D shell.
 */
export function coverageShapeProfile(p: CoverageShapeParams, arcSamples = 20):
    { ground: number[]; altitude: number[]; maxGroundRangeKm: number } {
  const peak = (p.peakElevationDeg ?? 20) * Math.PI / 180;
  const exp = p.rangeExponent ?? 1.4;
  const lo = clamp(p.minElevationDeg, 0, 88) * Math.PI / 180;
  const hi = Math.max(lo + 0.02, clamp(p.maxElevationDeg, 1, 89) * Math.PI / 180);
  const rangeAt = (theta: number): number => {
    const c = Math.cos(theta - peak);
    return c <= 0 ? 0 : Math.max(0, p.peakRangeKm) * c ** exp;
  };
  const ground: number[] = [0];      // apex (the sensor)
  const altitude: number[] = [0];
  let maxGround = 0;
  const n = Math.max(6, Math.round(arcSamples));
  for (let i = 0; i <= n; i++) {
    const theta = lo + (hi - lo) * (i / n);
    const d = rangeAt(theta);
    const g = d * Math.cos(theta);
    ground.push(g);
    altitude.push(d * Math.sin(theta));
    if (g > maxGround) maxGround = g;
  }
  ground.push(0);                    // close back to the apex
  altitude.push(0);
  return { ground, altitude, maxGroundRangeKm: maxGround };
}

/** Furthest covered ground distance for one target altitude (useful for 2D contours). */
export function groundReachAtAltitudeKm(spec: RadarSpec, sensorAltitudeKm: number,
                                        targetAltitudeKm: number,
                                        effectiveEarthK = STANDARD_REFRACTION_K): number {
  const max = coverageSearchMaxKm(spec);
  const probes = 512;
  let last = 0;
  let next = max;
  let seen = false;
  const covered = (d: number) => {
    const b = radarAltitudeBand(spec, sensorAltitudeKm, d, effectiveEarthK);
    return b != null && targetAltitudeKm >= b.minAltitudeKm - 1e-8
      && targetAltitudeKm <= b.maxAltitudeKm + 1e-8;
  };
  for (let i = 0; i <= probes; i++) {
    const d = max * i / probes;
    if (covered(d)) { last = d; seen = true; }
    else if (seen) { next = d; break; }
  }
  if (!seen) return 0;
  let lo = last, hi = next;
  for (let i = 0; i < 36; i++) {
    const mid = (lo + hi) / 2;
    if (covered(mid)) lo = mid; else hi = mid;
  }
  return lo;
}

/** Whether two returns occupy separable range or azimuth cells. */
export function assessRadarResolution(spec: RadarSpec, pose: RadarPose,
                                      a: RadarTarget, b: RadarTarget,
                                      env: RadarEnvironment = {}): ResolutionAssessment {
  const ga = radarGeometry(pose, a, env);
  const gb = radarGeometry(pose, b, env);
  const rangeDeltaM = Math.abs(ga.slantRangeKm - gb.slantRangeKm) * 1000;
  const elevA = ga.elevationDeg * Math.PI / 180;
  const elevB = gb.elevationDeg * Math.PI / 180;
  const azimuthDelta = angularDifferenceDeg(ga.bearingDeg, gb.bearingDeg) * Math.PI / 180;
  const cosAngle = Math.sin(elevA) * Math.sin(elevB)
    + Math.cos(elevA) * Math.cos(elevB) * Math.cos(azimuthDelta);
  const angularDelta = Math.acos(clamp(cosAngle, -1, 1)) * 180 / Math.PI;
  const hasRange = spec.rangeResolutionM != null && spec.rangeResolutionM > 0;
  const hasAz = spec.angularResolutionDeg != null && spec.angularResolutionDeg > 0;
  const rangeSeparated = hasRange ? rangeDeltaM >= spec.rangeResolutionM! : null;
  const angularSeparated = hasAz ? angularDelta >= spec.angularResolutionDeg! : null;
  return {
    resolved: !hasRange && !hasAz ? null : Boolean(rangeSeparated || angularSeparated),
    rangeSeparated,
    angularSeparated,
    rangeDeltaM,
    angularDeltaDeg: angularDelta,
  };
}
